<template>
  <v-form ref="form" v-model="valid" :lazy-validation="lazy">
    <notification-container
      v-if="!unnotification"
      class="mr-5 ml-5"
      :showOnlyLocalNotifications="true"
    ></notification-container>
    <v-alert class="py-1 my-1" color="cyan lighten-4">
      Datos Generales
    </v-alert>
    
    <v-row>
      <v-col class="py-0" cols="6">
        <expiration-date
          id="document_detail__fecha_creacion__date"
          v-model="documentForm.fechaSubidaS3"
          label="Fecha de creación"
          disabled
        ></expiration-date>
      </v-col>
      <v-col class="py-0" cols="6">
        <expiration-date 
          id="document_detail__fecha_expiracion__date"
          v-model="documentForm.fechaExpiracion"
          :disabled="readonly"
        ></expiration-date>
      </v-col>
    </v-row>

    <v-text-field
      id="document_detail__clave__text"
      autofocus
      label="Clave"
      v-model="documentForm.clave"
      :dense="dense"
      :readonly="readonly"
      :counter="15"
      :error-messages="getErrors('documentForm.clave', 'La Clave')"
      @input="$v.documentForm.clave.$touch()"
    ></v-text-field>

    <folder-picker
      ref="folderPiker"
      v-model="documentForm.idCarpeta"
      :readonly="readonly"
    ></folder-picker>

    <v-text-field
      id="document_detail__nombre__text"
      label="Nombre"
      v-model="documentForm.descripcion"
      :dense="dense"
      :readonly="readonly"
      :counter="100"
      :error-messages="getErrors('documentForm.descripcion', 'El Nombre')"
      @input="$v.documentForm.descripcion.$touch()"
    ></v-text-field>

    <document-type-combo
      id="document_detail__tipo_documento__combo"
      v-model="documentForm.idTipoDocumento"
      :readonly="readonly"
      :error-messages="
        getErrors('documentForm.idTipoDocumento', 'El Tipo de Documento')
      "
      @input="$v.documentForm.idTipoDocumento.$touch()"
    ></document-type-combo>

    <v-alert class="py-1 my-1" color="cyan lighten-4">
      Propietario
    </v-alert>
    <owner-avatar class="py-1" v-model="documentForm.idUsuario" />

    <v-alert class="py-1 my-1" color="cyan lighten-4">
      Estado
    </v-alert>
    <document-status viewIcon :status="documentForm.estatus" />

    <div class="text-right">
      <v-btn
        v-if="!readonly"
        id="document_detail__actualizar__button"
        small
        color="primary"
        @click.prevent="onUpdate"
        :loading="loading"
        >Actualizar</v-btn
      >
    </div>
  </v-form>
</template>

<script>
import DocumentStatus from '../components/commons/DocumentStatus'
import OwnerAvatar from '../components/commons/OwnerAvatar'
import FolderPicker from '@/modules/catalogs/components/FolderPicker'
import DocumentTypeCombo from '@/modules/catalogs/components/DocumentTypeCombo'
import ExpirationDate from '@/modules/documents/components/ExpirationDate'

import { required, maxLength } from 'vuelidate/lib/validators'
import VuelidateHelper from '@/utils/VuelidateHelper'
import {
  notSpecialCharsPunctuation,
  notSpecialCharsDescription
} from '@/utils/VuelidateCustoms'
import { pushNotification, messageErrorResponse } from '@/store/helpers'
import { documentsData, documentsActions } from '../store/documentsHelpers'
import SignerService from '@/modules/signers/services/SignerService'

export default {
  name: 'DocumentDetail',
  components: {
    OwnerAvatar,
    DocumentStatus,
    FolderPicker,
    ExpirationDate,
    DocumentTypeCombo
  },
  props: {
    document: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: true,
      loading: false,
      lazy: false,
      vuelidateHelper: null,
      documentForm: {},
      unnotification: { type: Boolean, default: false },
      expirationDateOriginal: null
    }
  },
  created() {
    this.setDocument()
    this.expirationDateOriginal = this.documentForm.fechaExpiracion
  },
  computed: {
    ...documentsData,
    dense() {
      return this.$vuetify.breakpoint.height <= 720
    }
  },
  watch: {
    document: function() {
      this.setDocument()
    }
  },
  methods: {
    ...documentsActions,
    onUpdate() {
      this.$v.$touch()
      if (this.$v.$anyError) {
        return
      }
      this.loading = true

      const data = {
        idDocumento: this.documentForm.idDocumento,
        clave: this.documentForm.clave,
        descripcion: this.documentForm.descripcion,
        fechaExpiracion: this.documentForm.fechaExpiracion,
        idTipoDocumento: this.documentForm.idTipoDocumento,
        idCarpeta: this.$refs.folderPiker.folderActive.idCarpeta
      }

      this.updateDocument(data)
        .then(result => {
          pushNotification(
            this.$store,
            result.data.successful.message,
            'info',
            'left',
            false,
            true
          )

          if (this.expirationDateOriginal !== this.documentForm.fechaExpiracion &&
            this.expirationDateOriginal < this.documentForm.fechaExpiracion){
            let signerService = new SignerService()
            this.documentForm.firmantes.forEach(element => {
              if (!element.firmado){
                let notificacion = {
                  id: element.id,
                  notificacion: 2
                }

                signerService.reminderSignerToDocument(notificacion)
                  .then(result => {
                    this.$emit('remindered')
                    pushNotification(
                      this.$store,
                      result.data.data.notifications[0].successful.message,
                      'info',
                      'left',
                      false,
                      true
                    )
                  })
                  .catch(err => {
                    this.notificationError(err)
                  })
              }
            })
          }
        })
        .catch(err => {
          this.notificationError(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getErrors(campo, label = '') {
      return this.vuelidateHelper != null
        ? this.vuelidateHelper.getErrors(campo, label)
        : ''
    },
    notificationError(msg) {
      pushNotification(
        this.$store,
        messageErrorResponse(msg) || this.msgDocumentServiceError,
        'error',
        'left',
        false,
        true
      )
    },
    setDocument() {
      if (this.document != null) {
        this.documentForm = Object.assign(new Object(), this.document)
        this.vuelidateHelper = new VuelidateHelper(this.$v)
      }
    }
  },
  validations: {
    documentForm: {
      clave: {
        maxLength: maxLength(15),
        notSpecialCharsPunctuation
      },
      descripcion: {
        required,
        maxLength: maxLength(100),
        notSpecialCharsDescription
      },
      idTipoDocumento: {
        required
      }
    }
  }
}
</script>

<style scoped>
.theme--dark.v-sheet .v-alert { color: #000}
</style>
